import type { Post } from "store/posts/postConstants";
import type { Integration, IntegrationType } from "store/user/userConstants";
import FacebookCard from "../FacebookCard";
import GenericCard from "../GenericCard";
import GoogleCard from "../GoogleCard";
import InstagramCard from "../InstagramCard";
import InstagramStoryCard from "../InstagramStoryCard";
import LinkedinCard from "../LinkedinCard";
import PinterestCard from "../PinterestCard";
import TikTokCard from "../TikTokCard";
import TwitterCard from "../TwitterCard";
import type {
  InstagramViewType,
  PreviewType,
  SupportedSocialCardKey,
} from "../types";
import { getIntegrationDetails } from "../utils/getIntegrationDetails";

interface SocialCardRendererProps {
  selectedPreview: PreviewType;
  selectedInstagramView: InstagramViewType;
  showInstagramSubSelector: boolean;
  integrations: (Omit<Integration, "type"> & { type: IntegrationType })[];
  caption: string;
  mediaUrls: string[];
  existingPost?: Post;
  title?: string;
  link?: string;
  isTemplateVideo: boolean;
}

const SocialCardRenderer = ({
  selectedPreview,
  selectedInstagramView,
  showInstagramSubSelector,
  integrations,
  caption,
  mediaUrls,
  existingPost,
  title,
  link,
  isTemplateVideo,
}: SocialCardRendererProps) => {
  // Determine the actual platformId needed based on main and sub-selection
  let targetPlatformId: SupportedSocialCardKey | undefined =
    selectedPreview === "Generic" ? undefined : selectedPreview;
  let isInstagramStoryView = false;

  if (
    selectedPreview === "instagram" &&
    showInstagramSubSelector &&
    selectedInstagramView === "story"
  ) {
    targetPlatformId = "instagramStory";
    isInstagramStoryView = true;
  } else if (selectedPreview === "linkedInProfile") {
    // If somehow 'linkedInProfile' is selected but should have been filtered, default to 'linkedIn' card
    // but fetch details for the profile. This case might not be reachable if filtering works correctly.
    targetPlatformId = "linkedInProfile";
  }

  // Fetch integration details once, allowing potential re-fetch for Instagram
  let integrationDetails = getIntegrationDetails(
    integrations,
    targetPlatformId
  );

  const commonProps = {
    caption,
    mediaUrls,
    username: integrationDetails?.username,
    profileUrl: integrationDetails?.profile_picture ?? undefined,
    postedAt: existingPost?.published_at ?? undefined,
    title: title ?? "Sample Title",
    isTemplateVideo,
    link,
  };

  switch (selectedPreview) {
    case "facebook":
      return <FacebookCard {...commonProps} platformId="facebook" />;
    case "instagram": {
      // This case now ONLY handles when the selected icon is 'instagram'
      targetPlatformId = "instagram"; // Default to post
      isInstagramStoryView = false;

      if (showInstagramSubSelector) {
        // Sub-selector is active, check its state
        isInstagramStoryView = selectedInstagramView === "story";
        targetPlatformId = isInstagramStoryView
          ? "instagramStory"
          : "instagram";
      }
      // If sub-selector is not shown, we are definitely showing the Post card
      // because the hook ensures selectedPreview is 'instagram' here.

      integrationDetails = getIntegrationDetails(
        integrations,
        targetPlatformId
      );

      return isInstagramStoryView ? (
        <InstagramStoryCard
          {...commonProps}
          platformId="instagramStory"
          username={integrationDetails?.username}
          profileUrl={integrationDetails?.profile_picture ?? undefined}
        />
      ) : (
        <InstagramCard
          {...commonProps}
          platformId="instagram"
          username={integrationDetails?.username}
          profileUrl={integrationDetails?.profile_picture ?? undefined}
        />
      );
    }
    case "instagramStory":
      // This case handles when the selected icon is 'instagramStory'
      // (which only happens when sub-selector is hidden)
      targetPlatformId = "instagramStory";
      integrationDetails = getIntegrationDetails(
        integrations,
        targetPlatformId
      );
      return (
        <InstagramStoryCard
          {...commonProps}
          platformId="instagramStory"
          username={integrationDetails?.username}
          profileUrl={integrationDetails?.profile_picture ?? undefined}
        />
      );
    case "linkedIn": {
      // Handles page or profile based on available integrations
      // Determine which actual LinkedIn integration is available, prioritizing page.
      const actualLinkedInType = integrations.find((i) => i.type === "linkedIn")
        ? "linkedIn"
        : integrations.find((i) => i.type === "linkedInProfile")
          ? "linkedInProfile"
          : undefined; // Should not happen if 'linkedIn' is selected

      // Fetch details for the actual available type
      const linkedInIntegrationDetails = getIntegrationDetails(
        integrations,
        actualLinkedInType
      );

      return (
        <LinkedinCard
          {...commonProps}
          username={linkedInIntegrationDetails?.username}
          profileUrl={linkedInIntegrationDetails?.profile_picture ?? undefined}
          platformId={actualLinkedInType ?? "linkedIn"} // Pass the actual type
        />
      );
    } // Pass correct ID
    case "twitter":
      return <TwitterCard {...commonProps} platformId="twitter" />;
    case "pinterest":
      return <PinterestCard {...commonProps} platformId="pinterest" />;
    case "tiktok":
      return <TikTokCard {...commonProps} platformId="tiktok" />;
    case "googleBusiness":
      return <GoogleCard {...commonProps} platformId="googleBusiness" />;
    case "Generic":
    default:
      // For Generic, we might not have a specific platformId
      return <GenericCard {...commonProps} />;
  }
};

export default SocialCardRenderer;
